import React, { useEffect, useState, useMemo } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { BulkBar, SelectAllCheckbox, RowCheckbox } from "@/components/BulkBar";
import { SortableHeader, useSort, makeComparator } from "@/components/SortableHeader";
import { AdminDateRangeControls } from "@/components/AdminDateRangeControls";
import { useAdminDateRange, valueInDateRange } from "@/lib/adminDateRange";

export default function Invoices() {
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const { dateFrom, dateTo, setDateFrom, setDateTo, showAll } = useAdminDateRange();
  const { sortKey, sortDir, toggleSort } = useSort("issued_at", "desc");

  const load = () => api.get("/invoices").then((r) => setAll(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const dated = all.filter((invoice) => valueInDateRange(invoice.issued_at || invoice.delivery_date || invoice.created_at, dateFrom, dateTo));
    let arr = tab === "all" ? dated : dated.filter((i) => i.status === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((i) =>
        (i.number || "").toLowerCase().includes(q) ||
        (i.customer_name || "").toLowerCase().includes(q) ||
        (i.business_name || "").toLowerCase().includes(q)
      );
    }
    arr.sort(makeComparator((i) => {
      switch (sortKey) {
        case "number": return (i.number || "").toLowerCase();
        case "customer": return (i.business_name || i.customer_name || "").toLowerCase();
        case "issued_at": return (i.issued_at || i.delivery_date || "");
        case "gallons": return Number(i.gallons || 0);
        case "total": return Number(i.total || 0);
        case "status": return (i.status || "").toLowerCase();
        default: return "";
      }
    }, sortDir));
    return arr;
  }, [all, tab, search, sortKey, sortDir, dateFrom, dateTo]);
  const datedInvoices = useMemo(
    () => all.filter((invoice) => valueInDateRange(invoice.issued_at || invoice.delivery_date || invoice.created_at, dateFrom, dateTo)),
    [all, dateFrom, dateTo],
  );

  const bulkDelete = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} invoice${ids.length === 1 ? "" : "s"}? Their tickets will be re-opened.`)) return;
    try {
      await api.post("/invoices/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length}`);
      setSelected([]); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <p className="overline">Billing</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">Invoices</h1>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Tabs value={tab} onValueChange={setTab} className="overflow-x-auto -mx-1 px-1 sm:overflow-visible">
          <TabsList className="rounded-sm inline-flex w-auto">
            <TabsTrigger value="all" data-testid="invoices-tab-all">All ({datedInvoices.length})</TabsTrigger>
            <TabsTrigger value="unpaid" data-testid="invoices-tab-unpaid">Unpaid ({datedInvoices.filter((i) => i.status === "unpaid").length})</TabsTrigger>
            <TabsTrigger value="paid" data-testid="invoices-tab-paid">Paid ({datedInvoices.filter((i) => i.status === "paid").length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input className="rounded-sm pl-9" placeholder="Search invoice # or customer…" value={search}
                 onChange={(e) => setSearch(e.target.value)} data-testid="invoice-search-input" />
        </div>
      </div>

      <AdminDateRangeControls dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} showAll={showAll} testIdPrefix="invoices" />

      <BulkBar selected={selected} allIds={filtered.map((i) => i.id)} onChangeSelected={setSelected}
               onBulkDelete={bulkDelete} testId="invoice-bulk-bar" />

      <div className="border border-border overflow-x-auto">
        <div className="min-w-[900px]">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline items-center">
          <div className="col-span-1"><SelectAllCheckbox selected={selected} allIds={filtered.map((i) => i.id)} onChange={setSelected} testId="invoice-select-all" /></div>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="number" onToggle={toggleSort} className="col-span-2" testId="sort-inv-number">Number</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="customer" onToggle={toggleSort} className="col-span-3" testId="sort-inv-customer">Customer</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="issued_at" onToggle={toggleSort} className="col-span-2" testId="sort-inv-issued">Delivery</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="gallons" onToggle={toggleSort} className="col-span-1" testId="sort-inv-gallons">Gallons</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="total" onToggle={toggleSort} className="col-span-2" testId="sort-inv-total">Total</SortableHeader>
          <SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="status" onToggle={toggleSort} className="col-span-1 justify-end" testId="sort-inv-status">Status</SortableHeader>
        </div>
        {filtered.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">{search ? "No matches." : "No invoices."}</div>}
        {filtered.map((inv) => (
          <div key={inv.id} className="grid grid-cols-12 px-6 py-3 border-b border-border last:border-b-0 hover:bg-secondary/40 items-center" data-testid={`invoice-row-${inv.number}`}>
            <div className="col-span-1"><RowCheckbox id={inv.id} selected={selected} onChange={setSelected} testId={`invoice-check-${inv.id}`} /></div>
            <Link to={`/admin/invoices/${inv.id}`} className="col-span-11 grid grid-cols-11">
              <div className="col-span-2 mono-num text-sm flex items-center gap-1.5">
                {inv.number}
                {inv.ai_review_flag && (
                  <span className="w-2 h-2 rounded-full bg-destructive inline-block shrink-0 animate-pulse"
                        title={`AI estimate variance ${inv.ai_variance_pct ?? "?"}% — needs review`}
                        data-testid={`ai-review-flag-${inv.number}`} />
                )}
              </div>
              <div className="col-span-3 font-medium">{inv.business_name || inv.customer_name}</div>
              <div className="col-span-2 mono-num text-sm text-muted-foreground">{fmtDate(inv.delivery_date)}</div>
              <div className="col-span-1 mono-num text-sm">{inv.gallons}</div>
              <div className="col-span-2 mono-num font-semibold">{fmtMoney(inv.total)}</div>
              <div className="col-span-1 text-right">
                <span className={`text-xs font-bold uppercase tracking-wider ${inv.status === 'paid' ? 'text-success' : 'text-destructive'}`}>
                  {inv.status}
                </span>
              </div>
            </Link>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}
